import { ChevronDown } from 'lucide-react';
import React, { useState, useRef, useEffect } from 'react';

export interface IGenericFilterProps {
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
  disabled?: boolean;
  className?: string;
}

const GenericFilter: React.FC<IGenericFilterProps> = ({
  label,
  value,
  options,
  onChange,
  disabled = false,
  className = '',
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Keyboard event handlers
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      setShowDropdown(false);
      setHighlightedIndex(-1);
      buttonRef.current?.focus();
      return;
    }

    if (!showDropdown) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        setShowDropdown(true);
        setHighlightedIndex(options.indexOf(value));
      }
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlightedIndex((prev) => (prev < options.length - 1 ? prev + 1 : 0));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : options.length - 1));
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (highlightedIndex >= 0) {
        onChange(options[highlightedIndex]);
      }
      setShowDropdown(false);
      setHighlightedIndex(-1);
      buttonRef.current?.focus();
    }
  };

  const handleSelect = (option: string) => {
    onChange(option);
    setShowDropdown(false);
    setHighlightedIndex(-1);
    buttonRef.current?.focus();
  };

  // Reset highlighted index when dropdown opens
  useEffect(() => {
    if (showDropdown) {
      setHighlightedIndex(options.indexOf(value));
    } else {
      setHighlightedIndex(-1);
    }
  }, [showDropdown, value, options]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        ref={buttonRef}
        onClick={() => {
          if (!disabled) {
            setShowDropdown(!showDropdown);
            if (!showDropdown) {
              setHighlightedIndex(options.indexOf(value));
            }
          }
        }}
        onKeyDown={disabled ? undefined : handleKeyDown}
        className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors w-48 ${
          disabled
            ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
            : 'bg-[#fff] dark:bg-[#1F2937] border-gray-300 text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-theme-500'
        }`}
        aria-haspopup='listbox'
        aria-expanded={showDropdown}
        aria-label={`Filter by ${label.toLowerCase()}. Current filter: ${value}`}
        disabled={disabled}
      >
        <span
          className={`text-sm font-medium truncate flex-1 text-left ${disabled ? 'text-gray-400' : 'text-gray-700'}`}
        >
          {label}: {value}
        </span>
        <ChevronDown
          size={16}
          className={`flex-shrink-0 ${disabled ? 'text-gray-400' : 'text-gray-500'}`}
          aria-hidden='true'
        />
      </button>

      {showDropdown && !disabled && (
        <div
          className='absolute top-full left-0 mt-1 w-48 bg-white border border-gray-300 rounded-lg shadow-lg z-20'
          role='listbox'
        >
          <div className='py-1'>
            {options.map((option, index) => (
              <button
                key={option}
                onClick={() => handleSelect(option)}
                onMouseEnter={() => setHighlightedIndex(index)}
                className={`w-full text-left px-4 py-2 text-sm hover:bg-theme-50 focus:outline-none focus:bg-theme-50 transition-colors ${
                  value === option ? 'bg-theme-50 text-theme-700' : 'text-gray-700'
                } ${highlightedIndex === index ? 'bg-theme-50' : ''}`}
                role='option'
                aria-selected={value === option}
              >
                <span className='truncate block' title={option}>
                  {option}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default GenericFilter;
